// Free shipping progress bar — display only.
//
// Reads the cart total after a cart change and repaints the bar. It never
// writes to the cart, never intercepts a form, and never blocks anything: if
// the read fails the bar simply keeps its last state, which is the value the
// server rendered.
//
// The cart drawer re-renders itself on change, so this is really for the
// product page, where the bar would otherwise go stale after an add to cart.

const money = (cents, currency) => {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
    }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
};

function paint(bar, totalCents) {
  const threshold = Number(bar.dataset.fsbThreshold || 0);
  if (!threshold) return;

  const total = Number(totalCents);
  if (!Number.isFinite(total)) return;

  const remaining = Math.max(threshold - total, 0);
  const percent = Math.min((total / threshold) * 100, 100);
  const reached = total >= threshold;

  const fill = bar.querySelector('[data-fsb-fill]');
  const text = bar.querySelector('[data-fsb-text]');
  const track = bar.querySelector('[data-fsb-track]');

  // Was the threshold crossed by this update, rather than already met when
  // the page loaded? Only the crossing is worth celebrating.
  const wasReached = bar.classList.contains('fsb--reached');
  const justUnlocked = reached && !wasReached && !('fsbNoCelebrate' in bar.dataset);

  if (fill) fill.style.width = `${percent}%`;
  if (track) track.setAttribute('aria-valuenow', String(Math.round(percent)));
  bar.classList.toggle('fsb--reached', reached);
  bar.dataset.fsbTotal = String(total);

  if (justUnlocked) {
    bar.classList.remove('fsb--just-unlocked');
    // Force a reflow so the class re-added below restarts the animation.
    void bar.offsetWidth;
    bar.classList.add('fsb--just-unlocked');
    setTimeout(() => bar.classList.remove('fsb--just-unlocked'), 1200);
  }

  if (text) {
    if (reached) {
      text.textContent = bar.dataset.fsbReachedText || '';
    } else if (total <= 0) {
      text.textContent = bar.dataset.fsbEmptyText || '';
    } else {
      text.textContent = (bar.dataset.fsbTemplate || '').replace(
        '[amount]',
        money(remaining, bar.dataset.fsbCurrency)
      );
    }
  }
}

let pending = false;

async function refresh() {
  if (pending) return;
  const bars = document.querySelectorAll('[data-fsb]');
  if (!bars.length) return;

  pending = true;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    const cart = await response.json();
    bars.forEach((bar) => paint(bar, cart.total_price));
  } catch (e) {
    // Leave the server-rendered state in place.
  } finally {
    pending = false;
  }
}

// Shopify's standard cart events. `shopify:cart:lines-update` is the one that
// fires on add to cart; the rest cover quantity, note and discount changes.
//
// The event carries a promise that settles once the update has actually been
// applied — reading the cart before it resolves returns the previous total,
// which is exactly the stale value this is meant to avoid.
const onCartEvent = (event) => {
  const promise = event?.promise;

  if (promise && typeof promise.then === 'function') {
    promise.then(() => refresh()).catch(() => refresh());
  } else {
    refresh();
  }
};

[
  'shopify:cart:lines-update',
  'shopify:cart:discount-update',
  'shopify:cart:note-update',
  'cart:update',
  'cart:refresh',
].forEach((name) => {
  document.addEventListener(name, onCartEvent);
});

document.addEventListener('DOMContentLoaded', () => {
  // A back/forward navigation can restore a stale page with a changed cart.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
});
